import React, { useState, useEffect } from 'react';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import tabs from '../../styles/DocentesStyles/NuevoDocenteModal.module.css';
import { createConceptoCobro, updateConceptoCobro } from '../../api/conceptosCobro';
import { getInstitutions } from '../../api/institutions';
import { getCampus } from '../../api/campus';
import { getSecciones } from '../../api/secciones';
import { getCycles } from '../../api/ciclosEscolares';

function NewConceptModal({ isOpen, onClose, onSave, editMode = false, duplicateMode = false, conceptoData = null }) {
  const [activeTab, setActiveTab] = useState('general');
  const [showHistorial, setShowHistorial] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    institucion: '',
    campus: '',
    cicloEscolar: 'Seleccionar Ciclo',
    seccion: '',
    descripcion: '',
    aplicacion: 'Asignado automáticamente',
    categoria: 'REGISTRO',
    periodicidad: 'Tetramestral',
    formaPago: 'Contado',
    subcategoria: 'INSCRIPCION',
    condicionesPago: '',
    precioBase: '$1,000',
    precioAnticipado: '$800',
    estado: 'ACTIVO'
  });

  const [instituciones, setInstituciones] = useState([]);
  const [campusList, setCampusList] = useState([]);
  const [seccionesList, setSeccionesList] = useState([]);
  const [ciclosList, setCiclosList] = useState([]);
  const [selectedSecciones, setSelectedSecciones] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCatalogos();
      
      // Cargar datos del concepto en modo edición o duplicado
      if ((editMode || duplicateMode) && conceptoData) {
        // Helper para formatear precio
        const formatPriceValue = (value) => {
          if (!value && value !== 0) return '$0';
          const numValue = parseFloat(value);
          if (isNaN(numValue)) return '$0';
          return `$${numValue.toLocaleString('en-US')}`;
        };
        
        setFormData({
          nombre: conceptoData.nombre || '',
          codigo: duplicateMode ? '' : (conceptoData.codigo || ''),
          institucion: conceptoData.id_institucion || '',
          campus: conceptoData.campus || '',
          cicloEscolar: conceptoData.ciclo || 'Seleccionar Ciclo',
          seccion: conceptoData.id_seccion || '',
          descripcion: conceptoData.descripcion || '',
          aplicacion: conceptoData.aplicacion === 'AUTOMATICO' ? 'Asignado automáticamente' : 'Manual',
          categoria: conceptoData.categoria || 'REGISTRO',
          periodicidad: conceptoData.periodicidad
            ? conceptoData.periodicidad.charAt(0) + conceptoData.periodicidad.slice(1).toLowerCase()
            : 'Tetramestral',
          formaPago: conceptoData.forma_pago
            ? conceptoData.forma_pago.charAt(0) + conceptoData.forma_pago.slice(1).toLowerCase()
            : 'Contado',
          subcategoria: conceptoData.subcategoria || 'INSCRIPCION',
          condicionesPago: !conceptoData.condiciones_pago || conceptoData.condiciones_pago === ''
            ? ''
            : conceptoData.condiciones_pago.toUpperCase().includes('LINEA')
              ? 'En Línea'
              : conceptoData.condiciones_pago.toUpperCase().includes('PRESENCIAL')
                ? 'Presencial'
                : '',
          precioBase: formatPriceValue(conceptoData.precio_base),
          precioAnticipado: formatPriceValue(conceptoData.precio_anticipado),
          estado: conceptoData.estado === 'ACTIVO' ? 'ACTIVO' : 'INACTIVO'
        });
        
        // No llamar aquí, solo en loadCatalogos tras cargar secciones
      }
    }
  }, [isOpen, editMode, duplicateMode, conceptoData]);

  const loadCatalogos = async () => {
    try {
      console.log('Cargando catálogos...');
      const [instData, campusData, seccionesData, ciclosData] = await Promise.all([
        getInstitutions(),
        getCampus(),
        getSecciones(),
        getCycles()
      ]);
      console.log('Instituciones:', instData);
      console.log('Campus:', campusData);
      console.log('Secciones:', seccionesData);
      console.log('Ciclos Escolares:', ciclosData);
      
      setInstituciones(instData);
      setCampusList(campusData);
      setSeccionesList(seccionesData);
      setCiclosList(ciclosData);

      // Si estamos editando o duplicando, intenta seleccionar la sección por id o por nombre
      if ((editMode || duplicateMode) && conceptoData) {
        let idStr = '';
        if (conceptoData.id_seccion) {
          idStr = String(conceptoData.id_seccion);
        } else if (conceptoData.seccion_nombre) {
          // Buscar el id de la sección por nombre
          const found = seccionesData.find(s =>
            s.nombre && conceptoData.seccion_nombre &&
            s.nombre.toLowerCase().trim() === conceptoData.seccion_nombre.toLowerCase().trim()
          );
          if (found) {
            idStr = String(found.id);
          }
        }
        if (idStr && seccionesData.some(s => String(s.id) === idStr)) {
          setSelectedSecciones([idStr]);
        } else {
          setSelectedSecciones(['']);
        }
      }

      // Seleccionar valores por defecto.
      // Si estamos en modo edición/duplicado y `conceptoData` tiene institución/campus, úsalos
      if ((editMode || duplicateMode) && conceptoData) {
        // Determinar institución
        const selectedInstitucion = conceptoData.id_institucion || formData.institucion || (instData[0] && instData[0].id) || '';

        // Determinar campus: intentar por id, luego por nombre (case-insensitive), luego fallback
        let selectedCampus = formData.campus || '';
        // Buscar por id (coerciendo a string para evitar desajustes de tipo)
        const foundById = campusData.find(c => String(c.id) === String(conceptoData.campus));
        if (foundById) {
          selectedCampus = foundById.id;
        } else {
          // Intentar por nombre: `conceptoData.campus`, `conceptoData.campus_nombre` u otras variantes
          const campusNameCandidates = [conceptoData.campus, conceptoData.campus_nombre, conceptoData.campusName];
          const normalizedCandidates = campusNameCandidates.filter(Boolean).map(s => String(s).toLowerCase().trim());
          const foundByName = campusData.find(c => {
            const name = (c.nombre || c.name || '').toLowerCase().trim();
            return normalizedCandidates.some(nc => nc === name);
          });
          if (foundByName) {
            selectedCampus = foundByName.id;
          } else if (!selectedCampus && campusData[0]) {
            selectedCampus = campusData[0].id;
          }
        }

        setFormData(prev => ({
          ...prev,
          institucion: selectedInstitucion,
          campus: selectedCampus || ''
        }));
      } else {
        // Seleccionar primera institución y campus por defecto si no hay ninguno seleccionado
        if (instData.length > 0 && !formData.institucion) {
          setFormData(prev => ({ ...prev, institucion: instData[0].id }));
        }
        if (campusData.length > 0 && !formData.campus) {
          setFormData(prev => ({ ...prev, campus: campusData[0].id }));
        }
      }
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const tabList = [
    { id: 'general', title: 'Información General' },
    { id: 'configuracion', title: 'Configuración del cobro' },
    { id: 'montos', title: 'Montos y Vigencia' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (value) => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Add $ and comma formatting
    if (numericValue) {
      const number = parseFloat(numericValue);
      if (!isNaN(number)) {
        return `$${number.toLocaleString('en-US')}`;
      }
    }
    return value;
  };

  const handleCurrencyChange = (field, value) => {
    const formattedValue = formatCurrency(value);
    handleInputChange(field, formattedValue);
  };

  // Datos de ejemplo para el historial de precios
  const historialData = [
    {
      fecha: '15/10/2025',
      precioBase: '$1,000',
      precioAnticipado: '$800',
      usuario: 'Nombre Apellido'
    },
    {
      fecha: '02/10/2025',
      precioBase: '$1,400',
      precioAnticipado: '$800',
      usuario: 'Nombre Apellido'
    },
    {
      fecha: '22/09/2024',
      precioBase: '$1,500',
      precioAnticipado: '$1,000',
      usuario: 'Nombre Apellido'
    }
  ];

  const handleShowHistorial = () => {
    setShowHistorial(true);
  };

  const handleBackToMontos = () => {
    setShowHistorial(false);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setShowHistorial(false); // Reset historial view when changing tabs
  };

  const handleModalClose = () => {
    setFormData({
      nombre: '',
      codigo: '',
      institucion: '',
      campus: '',
      cicloEscolar: 'Seleccionar Ciclo',
      seccion: '',
      descripcion: '',
      aplicacion: 'Asignado automáticamente',
      categoria: 'REGISTRO',
      periodicidad: 'Tetramestral',
      formaPago: 'Contado',
      subcategoria: 'INSCRIPCION',
      condicionesPago: 'En Línea',
      precioBase: '$1,000',
      precioAnticipado: '$800',
      estado: 'ACTIVO'
    });
    setSelectedSecciones([]);
    setShowHistorial(false); // Reset historial view when closing modal
    setActiveTab('general');
    onClose();
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Validar datos requeridos
      if (!formData.nombre) {
        alert('El nombre del concepto es requerido');
        setLoading(false);
        return;
      }
      
      if (!formData.institucion) {
        alert('Debe seleccionar una institución');
        setLoading(false);
        return;
      }
      
      if (!formData.campus) {
        alert('Debe seleccionar un campus');
        setLoading(false);
        return;
      }
      
      if (!selectedSecciones[0]) {
        alert('Debe seleccionar una sección académica');
        setLoading(false);
        return;
      }
      
      // Función para normalizar texto (quitar acentos y convertir a mayúsculas)
      const normalizeText = (text) => {
        return text
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toUpperCase();
      };
      
      // Función para parsear precio (quitar $ y comas)
      const parsePrice = (priceStr) => {
        if (!priceStr) return 0;
        const numericValue = priceStr.toString().replace(/[^\d.]/g, '');
        return parseFloat(numericValue) || 0;
      };
      
      // Preparar datos para enviar al backend
      // Normalizar condiciones de pago
      const normalizeCondicionesPago = (valor) => {
        if (!valor) return '';
        if (valor.toLowerCase().includes('línea')) return 'EN_LINEA';
        if (valor.toLowerCase().includes('presencial')) return 'PRESENCIAL';
        return valor.replace(/\s+/g, '_').toUpperCase();
      };

      const payload = {
        nombre_concepto: formData.nombre,
        id_institucion: parseInt(formData.institucion),
        campus: parseInt(formData.campus),
        aplicacion: formData.aplicacion === 'Asignado automáticamente' ? 'AUTOMATICO' : 'MANUAL',
        categoria: normalizeText(formData.categoria),
        subcategoria: formData.subcategoria,
        periodicidad: normalizeText(formData.periodicidad),
        activo: formData.estado === 'ACTIVO',
        fecha_vigencia_inicio: new Date().toISOString().split('T')[0], // Fecha actual en formato YYYY-MM-DD
        precio_base: parsePrice(formData.precioBase),
        precio_anticipado: parsePrice(formData.precioAnticipado),
        forma_pago: formData.formaPago ? formData.formaPago.toUpperCase() : '',
        condiciones_pago: normalizeCondicionesPago(formData.condicionesPago),
      };
      // Log para depuración: ver el valor de 'activo' que se envía
      console.log('Payload a enviar:', payload);
      // Agregar campos opcionales solo si tienen valor
      if (formData.codigo) {
        payload.codigo_concepto = formData.codigo;
      }
      
      if (selectedSecciones.length > 0 && selectedSecciones[0]) {
        payload.id_seccion = parseInt(selectedSecciones[0]);
      }
      
      if (formData.cicloEscolar && formData.cicloEscolar !== 'Seleccionar Ciclo') {
        payload.ciclo = formData.cicloEscolar;
      }
      
      if (formData.descripcion) {
        payload.descripcion = formData.descripcion;
      }
      
      console.log('Payload a enviar:', payload);
      
      let result;
      if (editMode && conceptoData && !duplicateMode) {
        // Modo edición
        result = await updateConceptoCobro(conceptoData.id, payload);
        console.log('Concepto actualizado:', result);
      } else {
        // Modo creación o duplicado
        result = await createConceptoCobro(payload);
        console.log('Concepto creado:', result);
      }
      
      if (onSave) {
        onSave(result);
      }
      window.alert(editMode ? '¡Concepto editado con éxito!' : (duplicateMode ? '¡Concepto duplicado con éxito!' : '¡Concepto registrado con éxito!'));
      handleModalClose();
    } catch (error) {
      console.error('Error al guardar concepto:', error);
      console.error('Detalle del error:', error.response?.data);
      // Mostrar errores de validación del backend
      const errorData = error.response?.data;
      let errorMsg = '';
      if (typeof errorData === 'object' && errorData !== null) {
        if (errorData.non_field_errors) {
          errorMsg = Array.isArray(errorData.non_field_errors) 
            ? errorData.non_field_errors.join('\n') 
            : errorData.non_field_errors;
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        } else {
          // Recorrer campos con error
          const msgs = [];
          for (const [key, val] of Object.entries(errorData)) {
            const fieldMsg = Array.isArray(val) ? val.join(', ') : val;
            msgs.push(`${key}: ${fieldMsg}`);
          }
          errorMsg = msgs.join('\n');
        }
      } else {
        errorMsg = error.message;
      }
      alert(`Error al guardar el concepto de cobro:\n${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleSeccion = (seccionId) => {
    setSelectedSecciones(prev => {
      if (prev.includes(seccionId)) {
        return prev.filter(id => id !== seccionId);
      } else {
        return [...prev, seccionId];
      }
    });
  };

  const renderGeneralTab = () => (
    <>
      <div className={styles.formGroup}>
        <label className={styles.label}>Nombre del concepto</label>
        <input className={styles.input} name="nombre" value={formData.nombre} onChange={e => handleInputChange('nombre', e.target.value)} required />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Código del concepto</label>
        <input className={styles.input} name="codigo" value={formData.codigo} maxLength={20} onChange={e => handleInputChange('codigo', e.target.value)} />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Institución</label>
        <select className={styles.select} name="institucion" value={formData.institucion} onChange={e => handleInputChange('institucion', e.target.value)} required style={{ color: '#222' }}>
          <option value="">— Selecciona una institución —</option>
          {Array.isArray(instituciones) && instituciones.length > 0 ? (
            instituciones.map(inst => (
              <option key={inst.id} value={inst.id}>{inst.name}</option>
            ))
          ) : (
            <option value="" disabled>Cargando instituciones...</option>
          )}
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Campus <span style={{ color: 'red' }}>*</span></label>
        <select className={styles.select} name="campus" value={formData.campus} onChange={e => handleInputChange('campus', e.target.value)} required style={{ color: '#222' }}>
          <option value="">— Selecciona un campus —</option>
          {Array.isArray(campusList) && campusList.length > 0 ? (
            campusList.map(campus => (
              <option key={campus.id} value={campus.id}>{campus.nombre}</option>
            ))
          ) : (
            <option value="" disabled>Cargando campus...</option>
          )}
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Ciclo Escolar</label>
        <select className={styles.select} name="cicloEscolar" value={formData.cicloEscolar} onChange={e => handleInputChange('cicloEscolar', e.target.value)} style={{ color: '#222' }}>
          <option value="Seleccionar Ciclo">— Selecciona un ciclo —</option>
          {Array.isArray(ciclosList) && ciclosList.length > 0 ? (
            [...ciclosList]
              .sort((a, b) => {
                // Extraer año y periodo (ej: 2020-2)
                const getParts = (c) => {
                  const str = c.nombre_ciclo || c.name || '';
                  const [anio, periodo] = str.split('-');
                  return { anio: parseInt(anio, 10) || 0, periodo: parseInt(periodo, 10) || 0 };
                };
                const ca = getParts(a);
                const cb = getParts(b);
                // Orden descendente por año, luego por periodo
                if (ca.anio !== cb.anio) return cb.anio - ca.anio;
                return cb.periodo - ca.periodo;
              })
              .map(ciclo => (
                <option key={ciclo.id} value={ciclo.nombre_ciclo || ciclo.name}>{ciclo.nombre_ciclo || ciclo.name}</option>
              ))
          ) : (
            <option value="" disabled>No hay ciclos disponibles</option>
          )}
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Sección <span style={{ color: 'red' }}>*</span></label>
        <select className={styles.select} name="seccion" value={selectedSecciones[0] || ''} onChange={e => setSelectedSecciones([e.target.value])} required style={{ color: '#222' }}>
          <option value="">— Selecciona una sección —</option>
          {Array.isArray(seccionesList) && seccionesList.length > 0 ? (
            seccionesList.map(s => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))
          ) : (
            <option value="" disabled>No hay secciones disponibles</option>
          )}
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Descripción</label>
        <textarea className={styles.textarea} rows={2} name="descripcion" value={formData.descripcion} onChange={e => handleInputChange('descripcion', e.target.value)} />
      </div>
    </>
  );

  const renderConfigurationTab = () => (
    <>
      <div className={styles.formGroup}>
        <label className={styles.label}>Aplicación del concepto</label>
        <select className={styles.select} name="aplicacion" value={formData.aplicacion} onChange={e => handleInputChange('aplicacion', e.target.value)} style={{ color: '#222' }}>
          <option value="Asignado automáticamente">Asignado automáticamente</option>
          <option value="Manual">Manual</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Subcategoría</label>
        <select className={styles.select} name="subcategoria" value={formData.subcategoria} onChange={e => handleInputChange('subcategoria', e.target.value)} style={{ color: '#222' }}>
          <option value="BAJA">Baja</option>
          <option value="BIBLIOTECA">Biblioteca</option>
          <option value="BOLETO DE CARRERA">Boleto de carrera</option>
          <option value="BOLETO DE GRADUACION">Boleto de graduación</option>
          <option value="CAMBIOS">Cambios</option>
          <option value="CARTA PASANTE">Carta pasante</option>
          <option value="CEDULA PROFESIONAL">Cédula profesional</option>
          <option value="CERTIFICACION">Certificación</option>
          <option value="COLEGIATURA">Colegiatura</option>
          <option value="CONSTANCIA">Constancia</option>
          <option value="DESBLOQUEO">Desbloqueo</option>
          <option value="ESTUDIO SOCIOECONOMICO">Estudio socioeconómico</option>
          <option value="EXAMEN EXTRAORDINARIO">Examen extraordinario</option>
          <option value="EXAMEN">Examen</option>
          <option value="GRADUACION">Graduación</option>
          <option value="INCORPORACION">Incorporación</option>
          <option value="INSCRIPCION">Inscripción</option>
          <option value="KARDEX">Kardex</option>
          <option value="MATERIA EXTRA">Materia extra</option>
          <option value="MATERIA INDIVIDUAL">Materia individual</option>
          <option value="P1">P1</option>
          <option value="P2">P2</option>
          <option value="P3">P3</option>
          <option value="P4">P4</option>
          <option value="P5">P5</option>
          <option value="PASANTIA">Pasantía</option>
          <option value="REGISTRO">Registro</option>
          <option value="SEGUNDO PLAGIO">Segundo plagio</option>
          <option value="SEGURO">Seguro</option>
          <option value="SERVICIOS EDUCATIVOS">Servicios educativos</option>
          <option value="SERVICIO">Servicio</option>
          <option value="TITULACION">Titulación</option>
          <option value="UNIFORME GASTRONOMIA">Uniforme gastronomía</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Categoría</label>
        <select className={styles.select} name="categoria" value={formData.categoria} onChange={e => handleInputChange('categoria', e.target.value)} style={{ color: '#222' }}>
          <option value="INICIO">Inicio</option>
          <option value="REGISTRO">Registro</option>
          <option value="PARCIALIDADES">Parcialidades</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Condiciones de pago</label>
        <select className={styles.select} name="condicionesPago" value={formData.condicionesPago} onChange={e => handleInputChange('condicionesPago', e.target.value)} style={{ color: '#222' }}>
          <option value="">— Selecciona una opción —</option>
          <option value="En Línea">En Línea</option>
          <option value="Presencial">Presencial</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Periodicidad</label>
        <select className={styles.select} name="periodicidad" value={formData.periodicidad} onChange={e => handleInputChange('periodicidad', e.target.value)} style={{ color: '#222' }}>
          <option value="Tetramestral">Tetramestral</option>
          <option value="Semestral">Semestral</option>
          <option value="Anual">Anual</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Forma de pago</label>
        <select className={styles.select} name="formaPago" value={formData.formaPago} onChange={e => handleInputChange('formaPago', e.target.value)} style={{ color: '#222' }}>
          <option value="Contado">Contado</option>
          <option value="Parcialidades">Parcialidades</option>
        </select>
      </div>
    </>
  );

  const renderMontosTab = () => (
    <>
      <div className={styles.formGroup}>
        <label className={styles.label}>Precio Base</label>
        <input className={styles.input} name="precioBase" value={formData.precioBase} onChange={e => handleCurrencyChange('precioBase', e.target.value)} placeholder="$1,000" />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Precio por pago anticipado</label>
        <input className={styles.input} name="precioAnticipado" value={formData.precioAnticipado} onChange={e => handleCurrencyChange('precioAnticipado', e.target.value)} placeholder="$800" />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.label}>Estado</label>
        <select className={styles.select} name="estado" value={formData.estado} onChange={e => handleInputChange('estado', e.target.value)} style={{ color: '#222' }}>
          <option value="ACTIVO">ACTIVO</option>
          <option value="INACTIVO">INACTIVO</option>
        </select>
      </div>
      <div className={styles.formGroup}>
        <button className={styles.primaryButton} type="button" onClick={handleShowHistorial} style={{ width: '100%', marginTop: 8 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={styles.clockIcon} style={{ marginRight: 8, verticalAlign: 'middle' }}>
            <path
              d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.31811 11.6819 1.33334 8 1.33334C4.31811 1.33334 1.33334 4.31811 1.33334 8C1.33334 11.6819 4.31811 14.6667 8 14.6667Z"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 4V8L10.6667 9.33333"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Historial de precios
        </button>
      </div>
    </>
  );

  const renderHistorialView = () => (
    <>
      <div style={{ padding: 0, width: '100%' }}>
        <h3 className={styles.modalTitle} style={{ marginBottom: 16 }}>Historial de precios</h3>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, padding: '24px 24px 0 24px' }}>
          <span style={{ fontSize: 14, color: '#555', marginRight: 16 }}>1 de 1</span>
          <button className={styles.secondaryButton} style={{ padding: '2px 10px', marginRight: 4 }} disabled>‹</button>
          <button className={styles.secondaryButton} style={{ padding: '2px 10px' }} disabled>›</button>
        </div>
        <div style={{ width: '100%', overflowX: 'auto', marginBottom: 24, padding: '0 24px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f6fa' }}>
                <th style={{ padding: '12px 8px', fontWeight: 600, fontSize: 13, color: '#223', borderBottom: '1px solid #e0e0e0', wordBreak: 'break-word', whiteSpace: 'normal' }}>Fecha de cambio</th>
                <th style={{ padding: '12px 8px', fontWeight: 600, fontSize: 13, color: '#223', borderBottom: '1px solid #e0e0e0', wordBreak: 'break-word', whiteSpace: 'normal' }}>Precio Base</th>
                <th style={{ padding: '12px 8px', fontWeight: 600, fontSize: 13, color: '#223', borderBottom: '1px solid #e0e0e0', wordBreak: 'break-word', whiteSpace: 'normal' }}>Precio por pago anticipado</th>
                <th style={{ padding: '12px 8px', fontWeight: 600, fontSize: 13, color: '#223', borderBottom: '1px solid #e0e0e0', wordBreak: 'break-word', whiteSpace: 'normal' }}>Usuario que modificó</th>
              </tr>
            </thead>
            <tbody>
              {historialData.map((item, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px 8px', fontSize: 13, wordBreak: 'break-word', whiteSpace: 'normal' }}>{item.fecha}</td>
                  <td style={{ padding: '12px 8px', fontSize: 13, wordBreak: 'break-word', whiteSpace: 'normal' }}>{item.precioBase}</td>
                  <td style={{ padding: '12px 8px', fontSize: 13, wordBreak: 'break-word', whiteSpace: 'normal' }}>{item.precioAnticipado}</td>
                  <td style={{ padding: '12px 8px', fontSize: 13, wordBreak: 'break-word', whiteSpace: 'normal' }}>{item.usuario}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ textAlign: 'right', padding: '0 24px 24px 24px' }}>
          <button className={styles.secondaryButton} onClick={handleBackToMontos} style={{ minWidth: 120, width: '100%', maxWidth: 200 }}>
            REGRESAR
          </button>
        </div>
        {/* Responsive adjustments for small screens */}
        <style>{`
          @media (max-width: 600px) {
            .${styles.modalTitle} { font-size: 1.1rem; }
            table { font-size: 12px !important; }
            th, td { padding: 6px 4px !important; word-break: break-word !important; white-space: normal !important; }
            button.${styles.secondaryButton} { font-size: 12px !important; min-width: 90px !important; }
            div[style*='padding: 24px'] { padding: 0 !important; }
            div[style*='padding: 24px 24px 0 24px'] { padding: 8px 8px 0 8px !important; }
            div[style*='padding: 0 24px'] { padding: 0 8px !important; }
            div[style*='padding: 0 24px 24px 24px'] { padding: 0 8px 8px 8px !important; }
          }
        `}</style>
      </div>
    </>
  );

  if (!isOpen) return null;

  return (
    <div className={styles.modalBackdrop} role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) handleModalClose(); }}>
      <div className={styles.modalContainer} role="dialog" aria-modal="true" aria-labelledby="concepto-title" onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 id="concepto-title" className={styles.modalTitle}>{duplicateMode ? 'Duplicar Concepto de Cobro' : (editMode ? 'Editar Concepto de Cobro' : 'Nuevo Concepto de Cobro')}</h2>
          <button className={styles.modalClose} aria-label="Cerrar" onClick={handleModalClose}>✕</button>
        </div>
        <div className={tabs.tabsRow} style={{ margin: '24px 0 24px 0' }}>
          {tabList.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`${tabs.tab} ${activeTab === tab.id ? tabs.tabActive : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.title}
            </button>
          ))}
        </div>
        <form className={styles.modalBody} onSubmit={e => { e.preventDefault(); handleSave(); }}>
          <div className={styles.modalGrid}>
            {showHistorial && activeTab === 'montos' ? (
              renderHistorialView()
            ) : (
              <>
                {activeTab === 'general' && renderGeneralTab()}
                {activeTab === 'configuracion' && renderConfigurationTab()}
                {activeTab === 'montos' && renderMontosTab()}
              </>
            )}
          </div>
          {!(showHistorial && activeTab === 'montos') && (
            <div className={styles.modalActions}>
              <button type="button" className={styles.secondaryButton} onClick={handleModalClose}>CANCELAR</button>
              <button type="submit" className={styles.primaryButton}>GUARDAR</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default NewConceptModal;
